// File: client/src/variant/CardEffect.js
//
// The CardEffect hook interface (DESIGN PRINCIPLE #3 & #4).
//
// A "card effect" is the BEHAVIOUR of a card. Card *data* (name, description,
// params) lives in the database (seeded from server/db/cardDefinitions.js) and is
// fetched by the client; the behaviour lives here, keyed by `effect_json.kind`.
// Adding a card with an existing `kind` requires NO code here. Adding a brand-new
// kind of behaviour means writing ONE effect object and registering it.
//
// An effect is a plain object implementing any subset of these OPTIONAL hooks. The
// effect engine (effectEngine.js) calls the hooks at well-defined points around a
// turn. Every hook is pure-ish: it receives a context and returns a (possibly
// modified) directive object; it must NOT mutate the chess engine's state directly
// (DESIGN PRINCIPLE #1 — the engine stays the source of truth). Returning
// `undefined` means "no change".
//
// ---------------------------------------------------------------------------
// HOOK REFERENCE
// ---------------------------------------------------------------------------
//
// kind: string                  // REQUIRED. Matches card_definitions.effect_json.kind.
//
// onPlay(ctx) -> patch | void
//   Called once, immediately when the card is played, before the player moves.
//   Use for instant effects (Skip, Reverse, Trade Hands, Pawn Riot) and for
//   arming turn-scoped state (e.g. "this turn you get N moves").
//   ctx: {
//     card,          // the full card definition object
//     gameState,     // current chess engine game state (read-only)
//     turnState,     // mutable per-turn variant scratch (see effectEngine)
//     playerColor,   // 'white' | 'black' — who is playing the card
//     opponentColor, // the other side
//   }
//   Returns a patch (see "PATCH SHAPE") or void.
//
// beforeMove(ctx) -> { allow: boolean, reason?: string } | void
//   Called before each individual move is committed this turn. May veto an
//   otherwise-legal move (e.g. "no moving the same piece twice in a row").
//   ctx adds: { from, to, moveIndexThisTurn, lastMovedPieceKey }.
//   Return { allow:false, reason } to reject; void/{allow:true} to permit.
//
// transformLegalMoves(ctx) -> moves[] | void
//   Called when the variant layer asks "what can this piece do this turn?".
//   Receives the engine's legal moves for a square and may return a replacement
//   list (Knight Fever overrides movement; Tiny Queen filters by distance).
//   ctx adds: { from, engineMoves }. Returning void keeps engineMoves unchanged.
//
// afterMove(ctx) -> patch | void
//   Called after each individual move is committed. ctx adds { from, to }.
//
// afterTurn(ctx) -> patch | void
//   Called once when the turn fully ends (all bonus moves used). Last chance to
//   adjust whose turn is next, etc.
//
// ---------------------------------------------------------------------------
// PATCH SHAPE (what hooks may return to influence the turn)
// ---------------------------------------------------------------------------
// All fields optional; the engine merges them:
//   {
//     bonusMoves: number,          // ADD this many extra moves to the current turn
//     skipOpponentTurns: number,   // opponent loses this many upcoming turns
//     swapControl: boolean,        // Reverse: flip which side the player controls
//     tradeHands: boolean,         // swap the two players' hands
//     immediateMoves: Move[],      // moves to apply right now (e.g. Pawn Riot)
//     rule: { ... },               // a turn-scoped rule flag the engine records
//     message: string,             // user-facing log line
//   }
//
// A Move is { from:[r,c], to:[r,c], promotion?, ... } — the same shape the chess
// engine consumes. The engine, not the effect, applies moves, preserving Principle #1.

/**
 * Identity effect: used as the fallback for an unknown `kind` so a card never
 * crashes the engine. It does nothing but log.
 */
export const NULL_EFFECT = {
  kind: '__null__',
  onPlay: ({ card }) => ({ message: `Card "${card?.name ?? '?'}" had no effect.` }),
};

/**
 * Validate that an object minimally looks like a CardEffect.
 * @param {object} effect
 * @returns {boolean}
 */
export function isValidEffect(effect) {
  return !!effect && typeof effect.kind === 'string' && effect.kind.length > 0;
}
